import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import RequirementUploadPage from '../projects/RequirementUploadPage';
import EpicsStoriesList from '../projects/EpicsStoriesList';
import JiraItemsPage from '../projects/JiraItemsPage';
import { Breadcrumbs, EmptyState, PageScaffold, Tabs } from '../ui';

const slugify = (name = '') =>
  String(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'project';

const tabFromPath = (pathname = '') => {
  if (pathname.includes('/requirements/jira')) return 'jira';
  if (pathname.includes('/requirements/epics')) return 'epics';
  return 'requirements';
};

const pathForTab = (tab) => {
  if (tab === 'jira') return 'requirements/jira';
  if (tab === 'epics') return 'requirements/epics';
  return 'requirements';
};

/**
 * DEFINE hub — Requirements / Epics & Stories / Jira
 */
const RequirementsHub = ({ initialTab = 'requirements' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => tabFromPath(location.pathname) || initialTab);
  const { selectedProject } = useProject();
  const { getProjectIdFromURL, getWorkspaceIdFromURL } = useWorkspaceRouting();

  const projectId = getProjectIdFromURL() || selectedProject?.id || null;
  const projectName = selectedProject?.name || 'Project';

  useEffect(() => {
    setTab(tabFromPath(location.pathname) || initialTab);
  }, [location.pathname, initialTab]);

  const jiraRouteId = useMemo(() => {
    if (!projectId) return null;
    return `${projectId}-${slugify(projectName)}`;
  }, [projectId, projectName]);

  const handleTabChange = useCallback(
    (nextTab) => {
      setTab(nextTab);
      const workspaceId = getWorkspaceIdFromURL();
      const subPath = pathForTab(nextTab);
      if (workspaceId) {
        navigate(buildWorkspaceDashboardPath(workspaceId, projectId, subPath));
      } else {
        navigate(`/dashboard/${subPath}`);
      }
    },
    [navigate, getWorkspaceIdFromURL, projectId]
  );

  return (
    <PageScaffold
      title="Requirements"
      description="Import requirements, browse epics & stories, and work with linked Jira items."
      breadcrumbs={<Breadcrumbs items={[{ label: 'Define' }, { label: 'Requirements' }]} />}
      compact
    >
      <Tabs
        activeId={tab}
        onChange={handleTabChange}
        tabs={[
          { id: 'requirements', label: 'Imported Requirements' },
          { id: 'epics', label: 'Epics & Stories' },
          { id: 'jira', label: 'Jira' },
        ]}
      />

      <div className="rt-legacy-embed rt-define-embed" style={{ marginTop: 12 }}>
        {!projectId ? (
          <EmptyState
            title="Select a project"
            description="Choose a project in the top bar to manage requirements, epics, and Jira items."
          />
        ) : (
          <>
            {tab === 'requirements' && (
              <RequirementUploadPage projectId={projectId} embedded />
            )}
            {tab === 'epics' && (
              <EpicsStoriesList projectId={projectId} embedded />
            )}
            {tab === 'jira' &&
              (jiraRouteId ? (
                <div className="rt-define-panel rt-define-panel--embed">
                  <JiraItemsPage embeddedIdAndName={jiraRouteId} />
                </div>
              ) : (
                <EmptyState
                  title="Unable to open Jira"
                  description="Project context is incomplete for Jira items."
                />
              ))}
          </>
        )}
      </div>
    </PageScaffold>
  );
};

export default RequirementsHub;
